import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { Panel } from '$tablet-app/src/app/components/Panel';
import Spacer from '$tablet-app/src/app/components/Spacer';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator';
import { useCurrentUserID } from '$tablet-app/src/app/users';
import { DashboardDeviceType } from '@akin-home/amplify';
import { toggleInList } from '@akin-home/arrays';
import {
  createTabletDashboardFromSelectedWidgets,
  DASHBOARD_WIDGET_DETAILS,
  DashboardWidgetType,
} from '@akin-home/dashboards';
import {
  useCreateDashboard,
  useTabletDashboardForUser,
  useUpdateDashboard,
} from '@akin-home/dashboards/react';
import { Text } from '@akin-home/text/native';
import { useEffect, useState } from 'react';
import { Dimensions, FlatList, StyleSheet } from 'react-native';
import styled from 'styled-components/native';
import { BottomRow } from './BottomRow';
import { WidgetOptionTile } from './WidgetOptionTile';

const SELECTABLE_WIDGET_DETAILS = DASHBOARD_WIDGET_DETAILS.filter(
  (elem) => elem.user_selectable
);

const SELECTABLE_TYPES = DASHBOARD_WIDGET_DETAILS.filter(
  (elem) => elem.user_selectable && !elem.required
).map((elem) => elem.type);

export const HomeScreenSetup = (
  props: RootStackScreenProps<'EditHomeScreen'>
) => {
  const {
    navigation: { navigate, goBack },
    route: { params },
  } = props;
  const show_back_button = params?.show_back_button || true;

  const user_id = useCurrentUserID();

  const [selected, setSelected] = useState<DashboardWidgetType[]>([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const createDashboard = useCreateDashboard();
  const updateDashboard = useUpdateDashboard();

  const { data, isLoading } = useTabletDashboardForUser(user_id);

  const onPressTile = (type: DashboardWidgetType) =>
    setSelected((selected) => toggleInList(selected, type).slice(0, 4));

  const onPressContinue = async () => {
    if (saving) return;

    setSaving(true);

    if (data) {
      const toUpdate = createTabletDashboardFromSelectedWidgets(selected);
      await updateDashboard.mutateAsync({
        input: {
          id: data.id,
          layout: JSON.stringify(toUpdate),
        },
      });

      sendBackgroundMessage(intent_labels.update_dashboard);
    } else {
      await createDashboard.mutateAsync({
        input: {
          user_id,
          device_type: DashboardDeviceType.tablet,
          name: 'User Dashboard: ' + user_id,
          layout: JSON.stringify(
            createTabletDashboardFromSelectedWidgets(selected)
          ),
        },
      });

      sendBackgroundMessage(intent_labels.create_dashboard);
    }

    setSaving(false);
    navigate('Main', { screen: 'Home' });
  };

  useEffect(() => {
    if (!loaded && !isLoading) {
      // Initialise the selected tiles with the current dashboard config
      const selectedTypes = data?.layout?.map((elem) => elem.type) ?? [];
      const currentlySelected = selectedTypes.filter((type) =>
        SELECTABLE_TYPES.includes(type)
      );
      setSelected(currentlySelected);

      setLoaded(true);

      sendBackgroundMessage(intent_labels.open_edit_dashboard, {
        context: {
          has_existing_dashboard: !!data,
        },
      });
    }
  }, [data, isLoading, loaded]);

  const [containerWidth, setContainerWidth] = useState(
    Dimensions.get('window').width
  );
  const numCols = Math.min(Math.max(Math.floor(containerWidth / 280), 1), 3);

  if (isLoading) {
    return <FullScreenLoader />;
  }

  return (
    <Container>
      <CenteredText variant="h3" weight="bold">
        {TITLE}
      </CenteredText>

      <Spacer height="16px" />

      <RestrictWidth
        onLayout={(event) =>
          setContainerWidth(event.nativeEvent.layout.width - 50)
        }
      >
        <CenteredText weight="semibold">{SUBTITLE}</CenteredText>
        <Spacer height="16px" />

        <ContentPanel>
          <CenteredText weight="semibold">
            Choose up to 4, scroll down to see more
          </CenteredText>

          <FlatList
            data={SELECTABLE_WIDGET_DETAILS}
            contentContainerStyle={styles.flat_list_content}
            numColumns={numCols}
            key={`${numCols}`}
            columnWrapperStyle={
              numCols > 1 ? styles.flat_list_column : undefined
            }
            renderItem={({ item }) =>
              item.user_selectable ? (
                <WidgetOptionTile
                  widget_option={item}
                  selected={selected.includes(item.type)}
                  onPress={onPressTile}
                />
              ) : null
            }
          />
        </ContentPanel>
      </RestrictWidth>

      <BottomRow
        onPressBack={show_back_button ? goBack : undefined}
        onPressContinue={onPressContinue}
        isSaving={saving}
        text={BOTTOM_TEXT}
      />
    </Container>
  );
};

const Container = styled.View`
  background-color: #f6f7f9;
  align-items: center;

  height: 100%;
  padding: 20px 20px 0;
`;

const RestrictWidth = styled.View`
  width: 1100px;
  max-width: 100%;
  align-items: center;
  flex: 1;
`;

const ContentPanel = styled(Panel)`
  padding: 16px 20px 0;
  gap: 10px;
  align-items: center;
  flex: 1;
  max-width: 100%;
`;

const CenteredText = styled(Text)`
  text-align: center;
`;

const styles = StyleSheet.create({
  flat_list_content: {
    gap: 22,
    paddingBottom: 22,
  },
  flat_list_column: {
    gap: 22,
  },
});

const TITLE = 'What do you want to see in your Home screen?';

const SUBTITLE =
  'While you will always be able to access any of the functions from the tabs at the bottom of the Home screen, you can set which ones display some information on the Home screen.';

const BOTTOM_TEXT =
  'You can change the things shown on your Home screen anytime in Settings.';
